import isEmpty from 'lodash/isEmpty';

import { GLOBAL_ASK_AICA_CONSTANTS } from './constants';
import { GlobalAskAicaState, GateOutcome, GATE_OUTCOME_TYPE } from './types';
import { DEFAULT_GLOBAL_SOURCES } from './constants';

const initialState: GlobalAskAicaState = {
    messages: [],
    isLoading: false,
    isLoadingChatHistory: false,
    error: null,
    currentCompany: null,
    companyLess: false,
    askBack: null,
    decline: null,
    conversationId: null,
    conversations: [],
    isLoadingConversations: false,
    selectedSources: DEFAULT_GLOBAL_SOURCES,
    isClearingChat: false,
    hasMoreData: false,
};

const addMessage = (state: GlobalAskAicaState, action: any): GlobalAskAicaState => ({
    ...state,
    messages: [...state.messages, action.payload],
});

const updateMessage = (state: GlobalAskAicaState, action: any): GlobalAskAicaState => {
    const { id, content, sources } = action.payload;
    return {
        ...state,
        messages: state.messages.map((message) =>
            message.id === id
                ? {
                      ...message,
                      content,
                      // sources arrives only on the final chunk — shallow-merge so it
                      // isn't dropped by an earlier content-only update (mirrors leads
                      // askAica/reducer.ts::updateMessage).
                      metadata: sources ? { ...message.metadata, sources } : message.metadata,
                  }
                : message,
        ),
    };
};

/** Mirrors the leads askAica/reducer.ts::setReasoning — accumulates the streamed
 * reasoning message onto state.reasoning for the in-flight turn. */
const setReasoning = (state: GlobalAskAicaState, action: any): GlobalAskAicaState => {
    const { reasoning, hasNewReasoning } = action.payload;
    if (isEmpty(reasoning) || !hasNewReasoning) return state;
    const latestReasoning = reasoning[reasoning.length - 1];
    return {
        ...state,
        reasoning: state.reasoning ? { ...state.reasoning, message: state.reasoning.message + latestReasoning.message } : latestReasoning,
    };
};

const initialiseMessageThread = (state: GlobalAskAicaState, action: any): GlobalAskAicaState => ({
    ...state,
    messages: action.payload,
    isLoadingChatHistory: false,
});

/**
 * Maps a raw gate_outcome (as read off the cumulative SSE metadata) onto the
 * individual gate-state fields. Only one of askBack/decline is ever non-null
 * at a time — a new gate outcome always supersedes the previous one.
 *
 * - resolved/continue/portfolio: the company is settled — set the chip, clear any
 *   open picker/notice.
 * - company_less: the chip is keyed off the wire's current_company, NOT the
 *   outcome type — a company_less turn (a general question, or a failed
 *   switch) can still carry a PINNED company on gate_outcome.current_company,
 *   and the chip must stay through it (RECUR-27369 R-2). Only a genuinely
 *   null/absent current_company clears the chip.
 * - ask_back: candidates offered (kind:"pick"). currentCompany/companyLess are
 *   left UNTOUCHED so a prior chip stays visible while the picker is open.
 * - decline: same as company_less — keyed off the wire's current_company, so a
 *   decline while a company is pinned keeps the chip; a decline with no prior
 *   company shows none.
 */
const applyGateOutcome = (state: GlobalAskAicaState, gateOutcome: GateOutcome): GlobalAskAicaState => {
    switch (gateOutcome.type) {
        case GATE_OUTCOME_TYPE.RESOLVED:
        case GATE_OUTCOME_TYPE.CONTINUE:
        case GATE_OUTCOME_TYPE.PORTFOLIO:
            return {
                ...state,
                currentCompany: gateOutcome.current_company,
                companyLess: false,
                askBack: null,
                decline: null,
            };
        case GATE_OUTCOME_TYPE.COMPANY_LESS:
            return {
                ...state,
                currentCompany: gateOutcome.current_company ?? null,
                companyLess: true,
                askBack: null,
                decline: null,
            };
        case GATE_OUTCOME_TYPE.ASK_BACK:
            return {
                ...state,
                askBack: gateOutcome.ask_back ?? null,
                decline: null,
            };
        case GATE_OUTCOME_TYPE.DECLINE:
            return {
                ...state,
                currentCompany: gateOutcome.current_company ?? null,
                decline: gateOutcome.decline ?? null,
                askBack: null,
            };
        default:
            return state;
    }
};

/** Gate-resolution fields reset by both New Chat and loading a different past
 * conversation — neither should carry a stale resolved company into the next turn. */
const clearGateState = (): Pick<GlobalAskAicaState, 'currentCompany' | 'companyLess' | 'askBack' | 'decline'> => ({
    currentCompany: null,
    companyLess: false,
    askBack: null,
    decline: null,
});

const resetGlobalChat = (state: GlobalAskAicaState): GlobalAskAicaState => ({
    ...state,
    ...clearGateState(),
    messages: [],
    isLoading: false,
    isLoadingChatHistory: false,
    error: null,
    conversationId: null,
    reasoning: undefined,
    hasMoreData: false,
});

const setConversations = (state: GlobalAskAicaState, action: any): GlobalAskAicaState => ({
    ...state,
    conversations: action.payload,
    isLoadingConversations: false,
});

const globalAskAicaReducer = (state: GlobalAskAicaState = initialState, action: any): GlobalAskAicaState => {
    switch (action.type) {
        case GLOBAL_ASK_AICA_CONSTANTS.ADD_MESSAGE:
            return addMessage(state, action);
        case GLOBAL_ASK_AICA_CONSTANTS.UPDATE_MESSAGE:
            return updateMessage(state, action);
        case GLOBAL_ASK_AICA_CONSTANTS.INITIALISE_MESSAGE_THREAD:
            return initialiseMessageThread(state, action);
        case GLOBAL_ASK_AICA_CONSTANTS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload };
        case GLOBAL_ASK_AICA_CONSTANTS.SET_IS_LOADING_CHAT_HISTORY:
            return { ...state, isLoadingChatHistory: action.payload };
        case GLOBAL_ASK_AICA_CONSTANTS.SET_ERROR:
            return { ...state, error: action.payload };
        case GLOBAL_ASK_AICA_CONSTANTS.SET_REASONING:
            return setReasoning(state, action);
        case GLOBAL_ASK_AICA_CONSTANTS.RESET_REASONING:
            return { ...state, reasoning: undefined };

        case GLOBAL_ASK_AICA_CONSTANTS.SET_GATE_OUTCOME:
            return applyGateOutcome(state, action.payload);
        case GLOBAL_ASK_AICA_CONSTANTS.SET_CURRENT_COMPANY:
            return { ...state, currentCompany: action.payload };
        case GLOBAL_ASK_AICA_CONSTANTS.SET_ASK_BACK:
            return { ...state, askBack: action.payload };

        case GLOBAL_ASK_AICA_CONSTANTS.SET_CONVERSATION_ID:
            return { ...state, conversationId: action.payload };
        case GLOBAL_ASK_AICA_CONSTANTS.RESET_GLOBAL_CHAT:
            return resetGlobalChat(state);
        case GLOBAL_ASK_AICA_CONSTANTS.RESET_GATE_STATE:
            return { ...state, ...clearGateState() };

        case GLOBAL_ASK_AICA_CONSTANTS.SET_CONVERSATIONS:
            return setConversations(state, action);
        case GLOBAL_ASK_AICA_CONSTANTS.SET_LOADING_CONVERSATIONS:
            return { ...state, isLoadingConversations: action.payload };

        case GLOBAL_ASK_AICA_CONSTANTS.SET_SELECTED_SOURCES:
            return { ...state, selectedSources: action.payload };

        case GLOBAL_ASK_AICA_CONSTANTS.SET_IS_CLEARING_CHAT:
            return { ...state, isClearingChat: action.payload };
        case GLOBAL_ASK_AICA_CONSTANTS.SET_HAS_MORE_DATA:
            return { ...state, hasMoreData: action.payload };

        default:
            return state;
    }
};

export default globalAskAicaReducer;
